// Money helpers for the Financial Health surface. int64 cents fields
// (burn_rate_cents, ar_balance_cents, funding_*_cents) arrive from the
// generated ryugu client as `bigint` — protobuf-es represents int64 as
// bigint, never `number`, so every conversion here stays in bigint/string
// space. Converting to `number` anywhere in the exact-value path would risk
// losing precision above 2^53 (~$90T in cents), a boundary a large enough
// aggregate funding figure could plausibly cross.
// BigInt(100) rather than the `100n` literal — thousand-sunny's tsconfig
// targets ES2017, and TS rejects bigint literal syntax below ES2020 (TS2737)
// even though the bigint type/runtime itself is fully supported.
const CENTS_PER_DOLLAR = BigInt(100);
const ZERO = BigInt(0);
/**
 * Exact cents → dollar string, e.g. 123456n -> "1,234.56", -50n -> "-0.50".
 * Pure bigint arithmetic (division/modulo), so no float rounding ever enters
 * the exact-value path.
 */ export function centsToDollarString(cents) {
    const negative = cents < ZERO;
    const abs = negative ? -cents : cents;
    const dollars = abs / CENTS_PER_DOLLAR;
    const remainder = abs % CENTS_PER_DOLLAR;
    const decimals = remainder.toString().padStart(2, '0');
    return `${negative ? '-' : ''}${dollars.toLocaleString('en-US')}.${decimals}`;
}
/**
 * Parses a user-typed decimal dollar string into exact cents — no
 * `parseFloat`/multiplication anywhere, so a value like "92233720368.55"
 * (well above 2^53 cents) round-trips exactly instead of drifting through
 * float multiplication by 100.
 *
 * Deliberate normalization/rejection rules (this is a financial input, so
 * silently coercing bad input to 0 would hide a typo as a real recorded
 * figure):
 *  - blank/whitespace-only → 0n (matches the rest of this directory's forms,
 *    where an untouched money field defaults to zero rather than an error).
 *  - thousands-separator commas are accepted and stripped before parsing,
 *    since `centsToDollarString`'s own output (and a user pasting a
 *    formatted figure) is comma-grouped — round-tripping that format is the
 *    point. But grouping must be well-formed (groups of exactly 3 digits,
 *    e.g. "1,234,567") → malformed grouping like "12,34" or "1,,234" throws
 *    rather than silently stripping to a plausible-looking but wrong value
 *    ("12,34" must not quietly become $1,234.00).
 *  - non-numeric or negative input → throws. A negative dollar amount isn't
 *    a valid burn rate/AR balance (the server rejects negative cents too —
 *    see financial_health.go's validateProgramFinancialMonth), and a
 *    garbled string is more dangerous silently zeroed than surfaced as an
 *    error the form can show inline.
 *  - more than 2 decimal places → throws (no sub-cent money).
 */ export function dollarsToCents(input) {
    const trimmed = input.trim();
    if (trimmed === '') return ZERO;
    if (trimmed.includes(',') && !/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(trimmed)) {
        throw new Error(`dollarsToCents: "${input}" has malformed thousands separators`);
    }
    const withoutCommas = trimmed.replace(/,/g, '');
    if (!/^\d+(\.\d+)?$/.test(withoutCommas)) {
        throw new Error(`dollarsToCents: "${input}" is not a non-negative decimal number`);
    }
    const [wholePart, fractionPart = ''] = withoutCommas.split('.');
    if (fractionPart.length > 2) {
        throw new Error(`dollarsToCents: "${input}" has more than 2 decimal places`);
    }
    return BigInt(wholePart || '0') * CENTS_PER_DOLLAR + BigInt(fractionPart.padEnd(2, '0'));
}
/**
 * Compact currency for headline figures, e.g. 624613105_00n -> "$624.6M".
 * Mirrors `fmtCompact` in FundingBar.tsx. Converting to `number` here is
 * safe (unlike the exact-value helpers above): compact display already
 * collapses to 1 decimal place at the M/B scale, so it carries no precision
 * claim beyond that rounding.
 */ export function formatCentsCompact(cents) {
    const negative = cents < ZERO;
    const dollars = Number(negative ? -cents : cents) / 100;
    const sign = negative ? '-' : '';
    if (dollars >= 1e9) return `${sign}$${(dollars / 1e9).toFixed(1)}B`;
    if (dollars >= 1e6) return `${sign}$${(dollars / 1e6).toFixed(1)}M`;
    if (dollars >= 1e3) return `${sign}$${Math.round(dollars / 1e3)}K`;
    return `${sign}$${dollars.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
}
